import { Edit, Warning } from '@mui/icons-material';
import {
  alpha,
  Box,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
  useTheme,
  IconButton,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent
} from '@mui/material';
import { useEffect, useState } from 'react';
import { httpRequest } from 'src/services/httpRequest';
import SaveIcon from '@mui/icons-material/Save';

// Draft.js imports
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState, convertToRaw } from 'draft-js';
import draftToHtml from 'draftjs-to-html';
import htmlToDraft from 'html-to-draftjs';
import 'react-draft-wysiwyg/dist/react-draft-wysiwyg.css';

import toast from 'react-hot-toast';
import { useSelector } from 'react-redux';
import { RootState } from 'src/redux-store/appStore';

const parameters = [
  { parameter: '##Link##', description: 'To show link for verify email.' },
  {
    parameter: '##ReturnToHomePage##',
    description: 'To return to the home page.'
  },
  { parameter: '##FullName##', description: "To add user's full name." },
  { parameter: '##Sender##', description: "To add sender's name." },
  { parameter: '##Password##', description: 'To add password.' },
  { parameter: '##ClickHere##', description: 'To add link of navigation.' },
  { parameter: '##Email##', description: 'To add email address of recipient.' },
  { parameter: '##RemovedFiles##', description: 'To add deleted file title.' },
  { parameter: '##ContractNo##', description: 'To add contract number.' },
  { parameter: '##AssignBids##', description: 'To add bid title.' },
  { parameter: '##BidTitle##', description: 'To add bid title.' },
  { parameter: '##Business##', description: 'To add business name.' },
  { parameter: '##Township##', description: 'To add township name.' },
  { parameter: '##Comment##', description: 'To add comments.' }
];

interface EmailTemplateData {
  EmailTemplateID: number;
  EmailSubject: string;
  EmailBody: string;
  Form: string;
  Event: string;
}

interface ApiResponse {
  status: string;
  message: string;
  data: {
    totalRecords: number;
    currentPage: number;
    totalPages: number;
    data: EmailTemplateData[];
  };
}

interface TypeOption {
  FormID: number;
  FormName: string;
}

interface ApiResponseFetchTemplateForms {
  status: string;
  message: string;
  data: TypeOption[];
}

interface EventOption {
  EventID: number;
  EventName: string;
}

const EmailTemplates: React.FC = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(3);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [emailTemplates, setEmailTemplates] = useState<EmailTemplateData[]>([]);
  const [formTypes, setFormTypes] = useState<TypeOption[]>([]);
  const [eventTypes, setEventTypes] = useState<EventOption[]>([]);
  const [selectedTemplate, setSelectedTemplate] =
    useState<EmailTemplateData | null>(null);

  const userDetails = useSelector((state: RootState) => state.user.userDetails);

  const userRole = userDetails.UserRole || '';

  // Editor state for Draft.js
  const [draftEditorState, setDraftEditorState] = useState(
    EditorState.createEmpty()
  );

  // Form data state
  const [formData, setFormData] = useState({
    EmailSubject: '',
    Form: '',
    Event: '',
    EmailBody: ''
  });

  const handlePageChange = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const fetchEmailTemplates = async () => {
    setLoading(true);
    try {
      const response: ApiResponse = await httpRequest(
        'GET',
        `/bid_portal/template/email?Page=${page + 1}&Size=${rowsPerPage}`
      );

      if (response?.data) {
        setEmailTemplates(response.data?.data || []);
        setTotalCount(response.data?.totalRecords || 0);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error?.message || 'Failed to fetch. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplateForms = async () => {
    try {
      const response: ApiResponseFetchTemplateForms = await httpRequest(
        'GET',
        `/bid_portal/template/forms`
      );

      if (response?.data) {
        setFormTypes(response.data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error?.message || 'Failed to fetch. Please try again later.');
    }
  };

  // Fetch event options based on selected form
  const fetchEventOptions = async (formID: number) => {
    try {
      const response = await httpRequest(
        'GET',
        `/bid_portal/template/events/${formID}`
      );

      if (response?.data) {
        setEventTypes(response.data || []);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error?.message || 'Failed to fetch. Please try again later.');
    }
  };

  useEffect(() => {
    fetchTemplateForms();
  }, []);

  useEffect(() => {
    fetchEmailTemplates();
  }, [page, rowsPerPage]);

  // Convert HTML to Draft.js editor state
  const htmlToEditorState = (html: string) => {
    if (!html) {
      return EditorState.createEmpty();
    }
    const contentBlock = htmlToDraft(html);
    const contentState = ContentState.createFromBlockArray(
      contentBlock.contentBlocks
    );
    return EditorState.createWithContent(contentState);
  };

  // Convert Draft.js editor state to HTML
  const convertEditorStateToHtml = (
    currentEditorState: EditorState
  ): string => {
    const rawContentState = convertToRaw(
      currentEditorState.getCurrentContent()
    );
    return draftToHtml(rawContentState);
  };

  const handleClear = () => {
    setFormData({
      EmailSubject: '',
      Form: '',
      Event: '',
      EmailBody: ''
    });
    setDraftEditorState(EditorState.createEmpty());
    setEventTypes([]);
    setSelectedTemplate(null);
    fetchEmailTemplates();
  };

  const handleFormSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    const { EmailSubject, Form, Event: eventName } = formData;

    // Get HTML from editor to preserve formatting
    const EmailBody = convertEditorStateToHtml(draftEditorState);

    const payload = {
      EmailSubject,
      EmailBody,
      Form,
      Event: eventName
    };

    try {
      setLoading(true);
      let response;
      if (selectedTemplate) {
        response = await httpRequest(
          'PATCH',
          `/bid_portal/template/email/${selectedTemplate.EmailTemplateID}`,
          payload
        );
      } else {
        response = await httpRequest(
          'POST',
          'bid_portal/template/email',
          payload
        );
      }

      if (response?.status === 'success') {
        toast.success(
          response?.message || 'Email Template has been saved successfully.'
        );
        handleClear();
      } else {
        toast.error(
          response?.message || 'Failed to fetch. Please try again later.'
        );
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error(error?.message || 'Failed to fetch. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Update event options when form changes
  const handleFormChange = (e: SelectChangeEvent<string>) => {
    const selectedForm = e.target.value;
    setFormData({
      ...formData,
      Form: selectedForm,
      Event: ''
    });

    // Fetch events for the selected form
    const formID = formTypes?.find(
      (form) => form.FormName === selectedForm
    )?.FormID;
    if (formID) {
      fetchEventOptions(formID);
    }
  };

  const handleEditorStateChange = (newEditorState: EditorState) => {
    setDraftEditorState(newEditorState);

    // Update form data with HTML to preserve formatting
    const htmlContent = convertEditorStateToHtml(newEditorState);
    setFormData((prevData) => ({
      ...prevData,
      EmailBody: htmlContent
    }));
  };

  const handleEdit = (template: EmailTemplateData) => {
    setSelectedTemplate(template);

    const appearData = {
      EmailSubject: template.EmailSubject || '',
      Form: template.Form || '',
      Event: template.Event || '',
      EmailBody: template.EmailBody || ''
    };

    setFormData(appearData);

    // Set editor state from HTML
    setDraftEditorState(htmlToEditorState(template.EmailBody || ''));

    const filteredTemplates = emailTemplates.filter(
      (emailTemplate) =>
        emailTemplate.EmailTemplateID === template.EmailTemplateID
    );

    setEmailTemplates(filteredTemplates);

    // Fetch event options when the form is selected
    const formID = formTypes?.find(
      (form) => form.FormName === template.Form
    )?.FormID;
    if (formID) {
      fetchEventOptions(formID);
    }
  };

  return (
    <Box
      sx={{
        p: 3,
        background: alpha(theme.palette.background.default, 0.8),
        minHeight: '100vh'
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 4
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: theme.palette.primary.main,
            position: 'relative',
            '&:after': {
              content: '""',
              position: 'absolute',
              bottom: -8,
              left: 0,
              width: 130,
              height: 4,
              borderRadius: 2,
              backgroundColor: theme.palette.primary.main
            }
          }}
        >
          Email Templates
        </Typography>
      </Box>

      <Paper
        sx={{
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
          border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table
                sx={{
                  '& .MuiTableCell-head': {
                    bgcolor: alpha(theme.palette.primary.main, 0.03),
                    fontWeight: 'bold',
                    color: theme.palette.text.primary,
                    py: 2
                  },
                  '& .MuiTableCell-body': {
                    py: 1.8
                  }
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>Subject</TableCell>
                    <TableCell>Body</TableCell>
                    <TableCell>Form</TableCell>
                    <TableCell>Event</TableCell>
                    {userRole === 'Admin' && (
                      <TableCell align="center">Actions</TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {emailTemplates && emailTemplates?.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center" sx={{ py: 5 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 2
                          }}
                        >
                          <Warning
                            sx={{
                              fontSize: 40,
                              color: alpha(theme.palette.text.secondary, 0.6)
                            }}
                          />
                          <Typography
                            variant="body1"
                            color="text.secondary"
                            sx={{ fontWeight: 500 }}
                          >
                            No email templates found.
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    emailTemplates &&
                    emailTemplates?.map((template) => (
                      <TableRow
                        key={template.EmailTemplateID}
                        hover
                        sx={{
                          '&:hover': {
                            bgcolor: alpha(theme.palette.primary.main, 0.03)
                          },
                          transition: 'background-color 0.2s ease',
                          '&:nth-of-type(even)': {
                            bgcolor: alpha(
                              theme.palette.background.default,
                              0.4
                            )
                          }
                        }}
                      >
                        <TableCell sx={{ fontWeight: 500 }}>
                          {template.EmailSubject || '-'}
                        </TableCell>
                        <TableCell
                          style={{ width: '50%', tableLayout: 'fixed' }}
                        >
                          <Box
                            sx={{
                              fontSize: '14px',
                              lineHeight: '1.5',
                              '& p': {
                                margin: '0 0 8px 0',
                                '&:last-child': {
                                  marginBottom: 0
                                }
                              },
                              '& ul, & ol': {
                                margin: '8px 0',
                                paddingLeft: '20px'
                              },
                              '& li': {
                                marginBottom: '4px'
                              },
                              '& strong': {
                                fontWeight: 'bold'
                              },
                              '& em': {
                                fontStyle: 'italic'
                              },
                              '& u': {
                                textDecoration: 'underline'
                              }
                            }}
                            dangerouslySetInnerHTML={{
                              __html: template.EmailBody || ''
                            }}
                          />
                        </TableCell>

                        <TableCell>{template.Form || ''}</TableCell>
                        <TableCell>{template.Event || ''}</TableCell>

                        {userRole === 'Admin' && (
                          <TableCell align="center">
                            <Box
                              sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                gap: 0.75
                              }}
                            >
                              <IconButton
                                onClick={() => handleEdit(template)}
                                size="small"
                                sx={{
                                  color: '#4285f4',
                                  backgroundColor: alpha('#4285f4', 0.05),
                                  '&:hover': {
                                    backgroundColor: alpha('#4285f4', 0.15)
                                  },
                                  transition: 'all 0.2s',
                                  padding: 1,
                                  border: `1px solid ${alpha('#4285f4', 0.2)}`
                                }}
                              >
                                <Edit fontSize="small" />
                              </IconButton>
                            </Box>
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <Box
              sx={{
                borderTop: `1px solid ${alpha(
                  theme.palette.primary.main,
                  0.1
                )}`,
                bgcolor: alpha(theme.palette.background.paper, 0.5)
              }}
            >
              <TablePagination
                component="div"
                count={totalCount}
                page={page}
                onPageChange={handlePageChange}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{
                  '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows':
                    {
                      fontWeight: 500
                    },
                  '& .MuiTablePagination-select': {
                    borderRadius: 1.5,
                    '&:focus': {
                      backgroundColor: alpha(theme.palette.primary.main, 0.05)
                    }
                  },
                  '& .MuiTablePagination-actions': {
                    '& .MuiIconButton-root': {
                      '&:hover': {
                        backgroundColor: alpha(theme.palette.primary.main, 0.1)
                      }
                    }
                  }
                }}
              />
            </Box>
          </>
        )}
      </Paper>

      {userRole === 'Admin' && (
        <Box mt={2}>
          <Grid container spacing={3}>
            {/* Left side: Form */}
            <Grid item xs={12} md={6}>
              <Card elevation={2} sx={{ borderRadius: 2 }}>
                <CardHeader
                  title="Email Template Form"
                  sx={{
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderBottom: `1px solid ${theme.palette.divider}`
                  }}
                />
                <CardContent>
                  <Box component="form" onSubmit={handleFormSubmit}>
                    <Grid container spacing={3}>
                      {/* First row */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Subject"
                          value={formData?.EmailSubject}
                          onChange={(e) =>
                            setFormData({
                              ...formData,
                              EmailSubject: e.target.value
                            })
                          }
                          required
                        />
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Form</InputLabel>
                          <Select
                            value={formData.Form}
                            onChange={handleFormChange}
                            label="Form"
                            sx={{ borderRadius: 1 }}
                          >
                            {formTypes &&
                              formTypes?.map((type) => (
                                <MenuItem
                                  key={type.FormID}
                                  value={type.FormName}
                                >
                                  {type.FormName}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <FormControl fullWidth required>
                          <InputLabel>Event</InputLabel>
                          <Select
                            value={formData.Event}
                            onChange={(e) =>
                              setFormData({
                                ...formData,
                                Event: e.target.value as string
                              })
                            }
                            label="Event"
                            sx={{ borderRadius: 1 }}
                          >
                            {eventTypes &&
                              eventTypes?.map((event) => (
                                <MenuItem
                                  key={event.EventID}
                                  value={event.EventName}
                                >
                                  {event?.EventName}
                                </MenuItem>
                              ))}
                          </Select>
                        </FormControl>
                      </Grid>

                      <Grid item xs={12}>
                        <Typography variant="subtitle1" sx={{ mb: 1 }}>
                          Email Body *
                        </Typography>
                        <Box
                          sx={{
                            border: `1px solid ${alpha(
                              theme.palette.primary.main,
                              0.23
                            )}`,
                            borderRadius: 1,
                            '& .rdw-editor-wrapper': {
                              backgroundColor: 'transparent'
                            },
                            '& .rdw-editor-toolbar': {
                              borderBottom: `1px solid ${alpha(
                                theme.palette.primary.main,
                                0.23
                              )}`,
                              borderRadius: '4px 4px 0 0',
                              padding: '6px',
                              backgroundColor: alpha(
                                theme.palette.background.default,
                                0.5
                              )
                            },
                            '& .rdw-editor-main': {
                              padding: '12px',
                              minHeight: '250px',
                              fontSize: '14px',
                              lineHeight: '1.5'
                            },
                            '& .rdw-option-wrapper': {
                              border: 'none',
                              borderRadius: '4px',
                              '&:hover': {
                                backgroundColor: alpha(
                                  theme.palette.primary.main,
                                  0.1
                                )
                              }
                            },
                            '& .rdw-option-active': {
                              backgroundColor: alpha(
                                theme.palette.primary.main,
                                0.2
                              )
                            }
                          }}
                        >
                          <Editor
                            editorState={draftEditorState}
                            onEditorStateChange={handleEditorStateChange}
                            placeholder="Enter email body content..."
                            toolbar={{
                              options: [
                                'inline',
                                'list',
                                'textAlign',
                                'history'
                              ],
                              inline: {
                                options: ['bold', 'italic', 'underline']
                              },
                              list: {
                                options: ['unordered', 'ordered']
                              },
                              textAlign: {
                                options: ['left', 'center', 'right']
                              }
                            }}
                          />
                        </Box>
                      </Grid>

                      {/* Buttons */}
                      <Grid item xs={12}>
                        <Box
                          sx={{
                            display: 'flex',
                            gap: 2,
                            justifyContent: 'flex-end',
                            mt: 3,
                            borderTop: `1px solid ${theme.palette.divider}`,
                            pt: 3
                          }}
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            type="submit"
                            disabled={loading}
                            size="large"
                            startIcon={
                              loading ? (
                                <CircularProgress size={20} />
                              ) : (
                                <SaveIcon />
                              )
                            }
                            sx={{
                              borderRadius: 1,
                              px: 4,
                              boxShadow: 2
                            }}
                          >
                            {selectedTemplate ? 'Update' : 'Submit'}
                          </Button>
                          <Button
                            variant="outlined"
                            size="large"
                            onClick={handleClear}
                            sx={{
                              borderRadius: 1,
                              px: 3
                            }}
                          >
                            Clear
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Right side: Table */}
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  borderRadius: 2,
                  overflow: 'hidden',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.08)}`
                }}
              >
                <TableContainer>
                  <Table
                    sx={{
                      '& .MuiTableCell-head': {
                        bgcolor: alpha(theme.palette.primary.main, 0.03),
                        fontWeight: 'bold',
                        color: theme.palette.text.primary,
                        py: 2
                      },
                      '& .MuiTableCell-body': {
                        py: 1.8
                      }
                    }}
                  >
                    <TableHead>
                      <TableRow>
                        <TableCell>Parameter</TableCell>
                        <TableCell>Description</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {parameters.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.parameter}</TableCell>
                          <TableCell>{item.description}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}
    </Box>
  );
};

export default EmailTemplates;
